import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Page Context System
 *
 * Allows dashboard pages to expose their current state to the global
 * DebateDrawer without tight coupling. Each page wraps its content in
 * <PageContextUpdater> to push structured context that the debate panel
 * can auto-capture.
 */

export interface PageContextData {
  /** Which page is currently active */
  pageType: 'research' | 'stock-analysis' | 'portfolio' | 'strategic' | 'other';
  /** Ticker symbols visible or relevant on the current page */
  tickers?: string[];
  /** The currently selected/focused ticker */
  selectedTicker?: string;
  /** Portfolio summary data (from portfolio page) */
  portfolioSummary?: {
    totalValue: number;
    positionCount: number;
    topHoldings: string[];
  };
  /** Previous research context or verdict text */
  researchContext?: string;
  /** Free-form page-specific context */
  customContext?: string;
}

interface PageContextType {
  /** Current page context data */
  context: PageContextData | null;
  /** Update the page context (called by pages) */
  setPageContext: (data: PageContextData) => void;
  /** Clear the page context */
  clearPageContext: () => void;
}

const PageContext = createContext<PageContextType>({
  context: null,
  setPageContext: () => {},
  clearPageContext: () => {},
});

export function PageContextProvider({ children }: { children: React.ReactNode }) {
  const [context, setContext] = useState<PageContextData | null>(null);

  const setPageContext = useCallback((data: PageContextData) => {
    setContext(data);
  }, []);

  const clearPageContext = useCallback(() => {
    setContext(null);
  }, []);

  return (
    <PageContext.Provider value={{ context, setPageContext, clearPageContext }}>
      {children}
    </PageContext.Provider>
  );
}

/**
 * Hook for the debate panel to read current page context.
 */
export function usePageContext() {
  return useContext(PageContext);
}

/**
 * Hook for pages to push their context.
 * Usage: const { setPageContext } = usePageContextUpdater();
 */
export function usePageContextUpdater() {
  const { setPageContext, clearPageContext } = useContext(PageContext);
  return { setPageContext, clearPageContext };
}
